import { Box, Typography, Button, useTheme, CircularProgress, Drawer, TextField, MenuItem, ButtonBase, Grid, Skeleton, Divider } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Product from '../components/Product';
import { useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import CheckIcon from '@mui/icons-material/Check';

const SearchDrawer = ({ open, onClose, title, children }) => (
  <Drawer
    anchor="bottom"
    open={open}
    onClose={onClose}
    sx={{
      '& .MuiDrawer-paper': {
        background: 'transparent',
        boxShadow: 'none'
      }
    }}
  >
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'end',
        justifyContent: 'center',
        p: 2
      }}
      onClick={onClose}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: '90%',
          maxWidth: '600px',
          bgcolor: 'background.paper',
          borderRadius: '25px',
          position: 'fixed',
          pb: 4,
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 48,
            height: 4,
            backgroundColor: 'grey.300',
            borderRadius: 2
          }
        }}
      >
        <Box sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
            {title}
          </Typography>
          {children}
        </Box>
      </Box>
    </Box>
  </Drawer>
);

const CollectionSkeleton = () => (
  <Box sx={{ mb: 10 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Skeleton variant="text" width={200} height={40} />
      <Skeleton variant="text" width={80} height={40} />
    </Box>
    <Box sx={{ display: 'flex', gap: 2, overflowX: 'hidden' }}>
      {[1, 2, 3].map((item) => (
        <Box key={item} sx={{ flexShrink: 0 }}>
          <Skeleton variant="rectangular" width={150} height={200} sx={{ borderRadius: 2 }} />
          <Skeleton variant="text" width={100} sx={{ mt: 1 }} />
          <Skeleton variant="text" width={140} />
        </Box>
      ))}
    </Box>
  </Box>
);

const Home = () => {
  const { user, api } = useAuth();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const theme = useTheme();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collectionsLoaded, setCollectionsLoaded] = useState(false);

  // Search drawer states
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchStep, setSearchStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState([]);
  const [selectedColor, setSelectedColor] = useState([]);

  const categories = user?.gender === 'women' 
    ? [
        { id: 'all', name: 'All' },
        { id: 'dresses', name: 'Dresses' },
        { id: 'tops', name: 'Tops' },
        { id: 'pants', name: 'Pants' },
        { id: 'skirts', name: 'Skirts' },
        { id: 'outerwear', name: 'Outerwear' },
        { id: 'shoes', name: 'Shoes' },
        { id: 'bags', name: 'Bags' },
        { id: 'accessories', name: 'Accessories' },
        { id: 'swimwear', name: 'Swimwear' },
      ]
    : [
        { id: 'all', name: 'All' },
        { id: 'tops', name: 'Tops' },
        { id: 'pants', name: 'Pants' },
        { id: 'outerwear', name: 'Outerwear' },
        { id: 'suits', name: 'Suits' },
        { id: 'shoes', name: 'Shoes' },
        { id: 'accessories', name: 'Accessories' },
        { id: 'underwear', name: 'Underwear' },
        { id: 'swimwear', name: 'Swimwear' },
        { id: 'any', name: 'Nothing specific' },
      ];

  const priceRanges = [
    { id: 'under50', name: 'Under $50' },
    { id: '50to100', name: '$50 - $100' },
    { id: '100to200', name: '$100 - $200' },
    { id: 'over200', name: 'Over $200' },
    { id: 'noLimit', name: 'Too rich to care' },
  ];

  const colors = [
    { id: 'black', name: 'Black' },
    { id: '#F3F3F3', name: 'White' },
    { id: '#005ABD', name: 'Blue' },
    { id: '#FF4C4C', name: 'Red' },
    { id: '#4FC683', name: 'Green' },
    { id: '#7F8294', name: 'Grey' },
    { id: '#FD3498', name: 'Pink' },
    { id: '#5F3312', name: 'Brown' },
    { id: '#FF963A', name: 'Orange' },
    { id: '#732DCE', name: 'Purple' },
    { id: 'any', name: 'Any color' },
  ];

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchStep(1);
    setSelectedCategory([]);
    setSelectedPrice([]);
    setSelectedColor([]);
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(prev => {
      if (categoryId === 'all' || categoryId === 'any') {
        return [categoryId];
      }
      if (prev.includes('all') || prev.includes('any')) {
        return [categoryId];
      }
      const newSelection = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      return newSelection.length === 0 ? [] : newSelection;
    });
  };

  const handlePriceSelect = (priceId) => {
    setSelectedPrice(prev => {
      if (priceId === 'noLimit') {
        return ['noLimit'];
      }
      if (prev.includes('noLimit')) {
        return [priceId];
      }
      const newSelection = prev.includes(priceId)
        ? prev.filter(id => id !== priceId)
        : [...prev, priceId];
      return newSelection.length === 0 ? [] : newSelection;
    });
  };

  const handleColorSelect = (colorId) => {
    setSelectedColor(prev => {
      if (colorId === 'any') {
        return ['any'];
      }
      if (prev.includes('any')) {
        return [colorId];
      }
      const newSelection = prev.includes(colorId)
        ? prev.filter(id => id !== colorId)
        : [...prev, colorId];
      return newSelection.length === 0 ? [] : newSelection;
    });
  };

  const handleNext = () => {
    if (searchStep < 3) {
      setSearchStep(searchStep + 1);
    } else {
      // Handle search submission
      handleSearchClose();
      console.log('Navigating to search with params:', {
        categories: selectedCategory,
        prices: selectedPrice,
        colors: selectedColor
      });
      // Navigate to search results with search parameters
      navigate('/search', { 
        state: { 
          categories: selectedCategory,
          prices: selectedPrice,
          colors: selectedColor
        },
        replace: true  // Replace current history entry
      });
    }
  };

  const getSearchStepTitle = (step) => {
    switch (step) {
      case 1:
        return "What are you looking for?";
      case 2:
        return "What's your budget?";
      case 3:
        return "Any specific colors?";
      default:
        return "Search";
    }
  };

  useEffect(() => {
    const fetchCollections = async () => {
      if (!user) return;
      
      try {
        console.log('Fetching collections...');
        const response = await api.get('/api/collections/lifestyle');
        console.log('Collections response:', response.data);
        setCollections(response.data.collections || []);
        setCollectionsLoaded(true);
      } catch (error) {
        console.error('Error fetching collections:', error);
        setError('Failed to load collections');
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [user, api]);

  useEffect(() => {
    if (collectionsLoaded) {
      const scrollContainers = document.querySelectorAll('[data-scroll-container]');
      scrollContainers.forEach(container => {
        container.scrollLeft = 0;
      });
    }
  }, [collectionsLoaded]);

  const handleViewAll = (collectionId) => {
    navigate(`/collection/${collectionId}`);
  };

  const getCollectionEmoji = (title) => {
    const emojiMap = {
      'City Explorer': '🌆',
      'Night Out': '🌙',
      'Active Life': '🏃',
      'Brunch & Beyond': '🍳',
      'Coffee Culture': '☕️'
    };
    
    return emojiMap[title] || '👗';
  };

  const formatProductForDisplay = (product) => {
    // Get the first image URL
    let imageUrl = '';
    if (product.media?.images?.length > 0) {
      imageUrl = typeof product.media.images[0] === 'string' 
        ? product.media.images[0] 
        : product.media.images[0]?.url || '';
    }

    // Get brand name
    const brandName = product.brand?.name || '';

    // Get pricing
    const currentPrice = product.price || 0;
    const previousPrice = product.original_price;

    return {
      id: product.id,
      image: imageUrl,
      brand: brandName,
      name: product.title,
      price: currentPrice,
      originalPrice: previousPrice,
      product: product  // Pass through the full product data
    };
  };

  if (loading) {
    return (
      <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto' }}>
        <Skeleton variant="text" width={200} height={60} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={300} height={30} sx={{ mb: 4 }} />
        <Skeleton variant="rectangular" height={50} sx={{ borderRadius: 2, mb: 4 }} />
        
        {[1, 2, 3].map((item) => (
          <CollectionSkeleton key={item} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto', mb: 10 }}>
      <Typography variant="h3" component="h2" gutterBottom>
        Hi {firstName} 👋
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 4 }}>
        Anything specific in your mind today? or just browsing?
      </Typography>
      
      {/* Search button */}
      <Button
        variant="contained"
        endIcon={<ArrowForwardIosIcon sx={{ width: '16px', height: '16px' }} />}
        size="large"
        onClick={() => setIsSearchOpen(true)}
        sx={{
          mb: 4,
          borderRadius: '12px',
          textTransform: 'none',
          width: '60%',
          height: '50px',
          backgroundColor: theme.palette.primary.brandLight,
          color: theme.palette.primary.main,
          "&:hover": {
            backgroundColor: theme.palette.primary.brandLight,
            color: theme.palette.primary.main
          }
        }}
      >
        I'm looking for...
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 4 }}>{error}</Typography>
      ) : collections.length === 0 ? (
        <Typography sx={{ mt: 4 }}>No collections available</Typography>
      ) : (
        <Box>
          {collections.map((collection, index) => (
            <Box key={collection.id} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" component="h2">
                  {collection.title} {getCollectionEmoji(collection.title)}
                </Typography>
                <Button
                  onClick={() => handleViewAll(collection.id)}
                  endIcon={<ArrowForwardIosIcon />}
                  sx={{
                    textTransform: 'none',
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  View All
                </Button>
              </Box>
              <Box 
                data-scroll-container
                sx={{ 
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  scrollSnapType: 'x mandatory',
                  '&::-webkit-scrollbar': { display: 'none' },
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                  scrollBehavior: 'smooth'
                }}
              >
                {collection.products.slice(0, 20).map((product) => (
                  <Box 
                    key={product.id}
                    sx={{ 
                      scrollSnapAlign: 'start',
                      flexShrink: 0
                    }}
                  >
                    <Product
                      {...formatProductForDisplay(product)}
                    />
                  </Box>
                ))}
              </Box>
              {index !== collections.length - 1 && (
                <Divider sx={{ mt: 4 }} />
              )}
            </Box>
          ))}
        </Box>
      )}

      <SearchDrawer
        open={isSearchOpen}
        onClose={handleSearchClose}
        title={getSearchStepTitle(searchStep)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {searchStep === 1 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select one or more categories
              </Typography>
              <Grid container spacing={1}>
                {categories.map((category) => (
                  <Grid item xs={6} key={category.id}>
                    <ButtonBase
                      onClick={() => handleCategorySelect(category.id)}
                      sx={{
                        width: '100%',
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: selectedCategory.includes(category.id) ? 'primary.main' : 'primary.brandLight',
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      {selectedCategory.includes(category.id) && (
                        <CheckIcon 
                          sx={{ 
                            width: '1rem',
                            height: '1rem',
                            color: 'white',
                            ml: 0.5
                          }} 
                        />
                      )}
                      <Typography 
                        variant="body4" 
                        color={selectedCategory.includes(category.id) ? 'white' : 'primary.main'} 
                        noWrap
                      >
                        {category.name}
                      </Typography>

                    </ButtonBase>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          
          {searchStep === 2 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select one or more price ranges
              </Typography>
              <Grid container spacing={1}>
                {priceRanges.map((range) => (
                  <Grid item xs={6} key={range.id}>
                    <ButtonBase
                      onClick={() => handlePriceSelect(range.id)}
                      sx={{
                        width: '100%',
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: selectedPrice.includes(range.id) ? 'primary.main' : 'primary.brandLight',
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      {selectedPrice.includes(range.id) && (
                        <CheckIcon 
                          sx={{ 
                            width: '1rem',
                            height: '1rem',
                            color: 'white',
                            ml: 0.5
                          }} 
                        />
                      )}
                      <Typography 
                        variant="body4" 
                        color={selectedPrice.includes(range.id) ? 'white' : 'primary.main'} 
                        noWrap
                      >
                        {range.name}
                      </Typography>
                    </ButtonBase>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          
          {searchStep === 3 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select one or more colors
              </Typography>
              <Grid container spacing={2}>
                {colors.map((color) => (
                  <Grid item xs={3} key={color.id}>
                    <ButtonBase
                      onClick={() => handleColorSelect(color.id)}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        borderRadius: 4,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          mt: 0.5,
                          backgroundColor: color.id === 'any' ? 'transparent' : color.id,
                          border: theme => color.id === '#F3F3F3' 
                            ? `1px solid ${theme.palette.grey[300]}`
                            : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          transition: 'all 0.2s',
                          transform: selectedColor.includes(color.id) ? 'scale(1)' : 'scale(1)',
                          ...(color.id === 'any' && {
                            backgroundImage: 'url("/any_color.png")',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }),
                        }}
                      >
                        {selectedColor.includes(color.id) && (
                          <CheckIcon 
                            sx={{ 
                              width: '1.2rem',
                              height: '1.2rem',
                              color: ['#F3F3F3'].includes(color.id) ? 'black' : 'white',
                            }} 
                          />
                        )}
                      </Box>
                      <Typography 
                        variant="body4"
                        sx={{
                          color: 'text.primary',
                          textAlign: 'center',
                          fontWeight: selectedColor.includes(color.id) ? 600 : 400,
                        }}
                      >
                        {color.name}
                      </Typography>
                    </ButtonBase>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (searchStep === 1 && selectedCategory.length === 0) ||
              (searchStep === 2 && selectedPrice.length === 0) ||
              (searchStep === 3 && selectedColor.length === 0)
            }
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              variant: 'button',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'primary.main',
              '&:disabled': {
                backgroundColor: 'primary.main',
                opacity: 0.6,
                color: 'white'
              }
            }}
          >
            {searchStep === 3 ? 'Show me what you got' : 'Proceed'}
            <ArrowForwardIosIcon sx={{ width: 16, height: 16 }} />
          </Button>
        </Box>
      </SearchDrawer>
    </Box>
  );
};

export default Home;